import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Where an extracted value came from. The form uses ``kind`` for the
 * DXF-wins-over-NL merge rule (NL prefill never overwrites a value that
 * a DXF/IFC extractor already produced). */
export type SourceKind = "dxf" | "ifc" | "nl";

export interface ExtractedField {
  value: unknown;
  confidence: number;
  label_hr?: string | null;
  source?: { file?: string; extractor?: string; layer?: string | null; note?: string | null };
}

interface ExtractionEntry {
  confidence: Record<string, ExtractedField>;
  sourceKind: SourceKind | null;
  warnings: string[];
}

const EMPTY_ENTRY: ExtractionEntry = {
  confidence: {},
  sourceKind: null,
  warnings: [],
};

interface ExtractionStore {
  /** Per-form-field confidence stamp from the last extraction run —
   * a MIRROR of ``byProject[activeProjectId]`` so existing consumers
   * (ConfidenceStamp, the form, the digest) keep their flat selectors. */
  confidence: Record<string, ExtractedField>;
  /** Which extractor family produced the current ``confidence`` map.
   * ``null`` when the store is empty. The NL component reads this to
   * decide whether to clobber existing values. */
  sourceKind: SourceKind | null;
  warnings: string[];
  /** Stamps keyed by project — switching projects (or a hard refresh)
   * must never show another project's confidence as this one's. */
  byProject: Record<string, ExtractionEntry>;
  activeProjectId: string | null;
  /** Point the mirror at a project's stamps. Call on entering a project
   * (DataPage mount, landing intake) BEFORE any extraction writes. */
  setActiveProject: (projectId: string | null) => void;
  setExtraction: (
    confidence: Record<string, ExtractedField>,
    warnings?: string[],
    sourceKind?: SourceKind,
  ) => void;
  /** Merge a second extraction wave on top of the existing one without
   * clobbering fields whose ``sourceKind`` is engineering-grade (DXF/IFC).
   * Used by the NL prefill path so a voice memo can fill the *gaps* a
   * dropped DXF left, but never overwrites a measurement the DXF
   * already produced. */
  mergeExtraction: (
    incoming: Record<string, ExtractedField>,
    incomingSourceKind: SourceKind,
    warnings?: string[],
  ) => void;
  /** Q1 — the user vouched for an amber value: confidence becomes 1.0 and
   * the stamp reads "potvrđeno". The original extractor is kept in
   * ``source`` so the tooltip can still say where the value came from. */
  confirmField: (field: string) => void;
  /** #20 — the user TYPED over the value: the extraction stamp no longer
   * describes what's in the input, so it goes away entirely (this is the
   * "ručni unos briše oznaku" promise the tooltip made all along). Fired
   * from the form's DOM onChange, so programmatic ``setValue`` prefills
   * never clear their own fresh stamps. */
  clearField: (field: string) => void;
  clear: () => void;
}

/** Marker written into ``source.note`` by ``confirmField`` — the stamp and
 * the tests key off this exact string. */
export const CONFIRMED_NOTE = "potvrdio korisnik";

/** Mirror + per-project write in one update. */
function write(state: ExtractionStore, entry: ExtractionEntry) {
  return {
    ...entry,
    byProject: state.activeProjectId
      ? { ...state.byProject, [state.activeProjectId]: entry }
      : state.byProject,
  };
}

export const useExtractionStore = create<ExtractionStore>()(
  persist(
    (set) => ({
      ...EMPTY_ENTRY,
      byProject: {},
      activeProjectId: null,
      setActiveProject: (projectId) =>
        set((state) => ({
          activeProjectId: projectId,
          ...(projectId ? state.byProject[projectId] ?? EMPTY_ENTRY : EMPTY_ENTRY),
        })),
      setExtraction: (confidence, warnings = [], sourceKind = "dxf") =>
        set((state) => write(state, { confidence, warnings, sourceKind })),
      mergeExtraction: (incoming, incomingSourceKind, warnings = []) =>
        set((state) => {
          // Engineering-grade sources (DXF/IFC) always beat NL. So when an
          // NL wave arrives, skip any field that's already stamped DXF/IFC.
          // When a DXF/IFC wave arrives, it replaces NL values for the same
          // field (last engineering-grade write wins).
          const merged: Record<string, ExtractedField> = { ...state.confidence };
          const existingKind = state.sourceKind;
          const isEngineering = (k: SourceKind | null) => k === "dxf" || k === "ifc";

          for (const [field, payload] of Object.entries(incoming)) {
            const existing = merged[field];
            if (
              existing &&
              incomingSourceKind === "nl" &&
              isEngineering(existingKind)
            ) {
              continue;
            }
            merged[field] = payload;
          }

          return write(state, {
            confidence: merged,
            sourceKind: isEngineering(incomingSourceKind)
              ? incomingSourceKind
              : isEngineering(existingKind)
                ? existingKind
                : "nl",
            warnings: [...state.warnings, ...warnings],
          });
        }),
      confirmField: (field) =>
        set((state) => {
          const existing = state.confidence[field];
          if (!existing) return state;
          return write(state, {
            confidence: {
              ...state.confidence,
              [field]: {
                ...existing,
                confidence: 1.0,
                source: { ...existing.source, note: CONFIRMED_NOTE },
              },
            },
            sourceKind: state.sourceKind,
            warnings: state.warnings,
          });
        }),
      clearField: (field) =>
        set((state) => {
          if (!state.confidence[field]) return state;
          const confidence = { ...state.confidence };
          delete confidence[field];
          return write(state, {
            confidence,
            sourceKind: state.sourceKind,
            warnings: state.warnings,
          });
        }),
      clear: () => set((state) => write(state, EMPTY_ENTRY)),
    }),
    { name: "permitpilot.extraction.v1" },
  ),
);
